import { useEffect, useState } from 'react';

/** Eased value between from and to for progress 0…1 (ease-out, whole numbers). */
export function countUpValue(from: number, to: number, progress: number): number {
  const p = Math.max(0, Math.min(1, progress));
  const eased = 1 - Math.pow(1 - p, 3);
  return Math.round(from + (to - from) * eased);
}

export function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Counts from `from` to `to` over `durationMs` (design: "Count the stars up … about one second").
 * Jumps straight to `to` when the user prefers reduced motion.
 */
export function useCountUp(from: number, to: number, durationMs = 1000, key?: unknown): number {
  const [value, setValue] = useState(prefersReducedMotion() ? to : from);
  useEffect(() => {
    if (prefersReducedMotion() || from === to) {
      setValue(to);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = (now - start) / durationMs;
      setValue(countUpValue(from, to, p));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    setValue(from);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [from, to, durationMs, key]);
  return value;
}
